/* eslint-disable react/no-unescaped-entities */
import styled from 'styled-components';
import { useRouter } from 'next/router';
import Image from 'next/image';
import Link from 'next/link';

import ButtonLink from '../../components/atoms/button-link/button-link';
import LoadingIcon from '../../components/atoms/loading-icon/loading-icon';
import TabTitle from '../../components/atoms/tab-title/tab-title';
import { MetaPages } from '../../enums/pages';
import SocialMediaShareSection from '../../components/molecules/social-media-share-section/social-media-share-section';
import { getFeaturedFriendsOfTheKitchenPageDetails } from '../../services/inspirations-and-planning';
import { FriendsOfTheKitchenPageDetailsType } from '../../types/content';
import Breadcrumbs from '../../components/atoms/breadcrumbs/breadcrumbs';
import ContainerWithSidebar from '../../components/atoms/container-with-sidebar/container-with-sidebar';
import MarginSpacing from '../../components/atoms/margin-spacing/margin-spacing';
import FeaturesSidebar from '../../components/molecules/features-sidebar/features-sidebar';
import { pageRoute } from '../../config/routes/pages';
import { NewsAndEventsModel } from '../../interface/news-and-events';
import { getFeaturedNewsAndEvents } from '../../services/news';
import Carousel from '../../components/molecules/carousel/carousel';
import { linkRoutes } from '../../config/routes/links';
import { strapiAssetBaseUrl } from '../../config/routes/assets';

const Container = styled.div`
  p {
    font-family: MuseoSans-100;
    font-size: 14px;
    font-weight: 200 !important;
    margin: 20px 0;
    text-align: justify;
  }

  caption {
    font-family: MuseoSans-100;
    font-size: 14px;
    font-weight: 200 !important;
    font-style: italic;
  }

  em {
    font-size: 12px;
  }

  p strong {
    font-family: MuseoSans-500;
  }

  ul {
    list-style-type: circle;
    padding-left: 40px;
  }

  h1 {
    font-family: ChronicleDisplayLight;
    font-size: 35px;
    font-weight: 300 !important;
    text-transform: uppercase;
    line-height: 2.25rem;
  }

  a {
    text-decoration-line: underline;
  }

  a:hover {
    color: #0081c6;
  }
`;

type IndexProps = {
  featuredFriendsOfTheKitchenPageDetails: FriendsOfTheKitchenPageDetailsType[];
  featuredNewsAndEvents: NewsAndEventsModel[];
  newsAndEventsArticle: NewsAndEventsModel;
};

const links = {
  showroom: 'https://my.matterport.com/show/?m=Boe2AFEuzJe',
  dualRefSystemArticle: 'https://subzero-wolf.com.ph/news-and-events/26659',
  inductionArticle: 'https://subzero-wolf.com.ph/news-and-events/26657',
  bookAnAppointment:
    'https://calendly.com/fgi-kitchen-bookings?utm_source=Instagram&utm_medium=Bio_Link&utm_campaign=Calendly_Appointments&utm_content=SZWF'
};

const carouselImages = [
  {
    src: `${strapiAssetBaseUrl}/Josh_Boutwood_Helm_9b477b5412.jpg`,
    caption: 'Josh Boutwood, Helm',
    captionOnTop: true
  },
  {
    src: `${strapiAssetBaseUrl}/Don_Baldosano_Linamnam_1f68030d12.jpg`,
    caption: 'Don Baldosano, Linamnam',
    captionOnTop: true
  },
  {
    src: `${strapiAssetBaseUrl}/Carlos_Villaflor_Gallery_by_Chele_f87e285a92.jpg`,
    caption: 'Carlos Villaflor, Gallery by Chele Gonzalez',
    captionOnTop: true
  },
  {
    src: `${strapiAssetBaseUrl}/Charles_Montanez_Mamacita_9756cecbe5.jpg`,
    caption: 'Charles Montanez, Mamacita',
    captionOnTop: true
  },
  {
    src: `${strapiAssetBaseUrl}/Aaron_Isip_Kasa_Palma_9e295fc285.jpg`,
    caption: 'Aaron Isip, Kasa Palma',
    captionOnTop: true
  },
  {
    src: `${strapiAssetBaseUrl}/Timothy_Flores_and_Genie_Kwon_Kasama_Chicago_0bcd57cbfa.jpg`,
    caption: 'Timothy Flores and Genie Kwon, Kasama Chicago',
    captionOnTop: true
  },
  {
    src: `${strapiAssetBaseUrl}/Angelo_Comsti_Offbeat_Bistro_aacec01349.jpg`,
    caption: 'Angelo Comsti, Offbeat Bistro',
    captionOnTop: true
  },
  {
    src: `${strapiAssetBaseUrl}/JP_Angelo_Sarsa_MNL_80cca58132.jpg`,
    caption: 'JP Angelo, Sarsa MNL',
    captionOnTop: true
  },
  {
    src: `${strapiAssetBaseUrl}/Jordy_Navarra_Toyo_e2640fc4cc.jpg`,
    caption: 'Jordy Navarra, Toyo',
    captionOnTop: true
  }
];

function ArticleContent() {
  return (
    <article className="flex flex-col gap-5">
      <div>
        <h1>CELEBRATING THE STARS OF THE KITCHEN</h1>
        <p>
          Sub-Zero and Wolf proudly joins the MICHELIN Guide Manila and Environs
          | Cebu 2026 as an exclusive event partner in the Kitchen Appliances
          category – marking a new chapter in the story of Filipino culinary
          excellence.
        </p>
        <div className="flex flex-col gap-1">
          <div className="flex flex-col md:flex-row gap-4">
            <Image
              alt="Michelin Guide Ceremony Manila and Environs | Cebu 2026"
              className="object-cover"
              height={500}
              src={`${strapiAssetBaseUrl}/1_690b4287e5.jpg`}
              width={700}
            />
            <Image
              alt="Michelin Guide Ceremony Manila and Environs | Cebu 2026"
              className="object-cover"
              height={500}
              src={`${strapiAssetBaseUrl}/2_77c97842ab.jpg`}
              width={700}
            />
          </div>
          <caption>
            [L-R] MICHELIN Guide Ceremony main stage, MICHELIN Guide Management
            and Philippine Department of Tourism.
          </caption>
          <caption>Photo credit: MICHELIN Guide.</caption>
        </div>
        <p>
          Last February 2025, anonymous inspectors of the MICHELIN Guide arrived
          in the Philippines to begin their culinary journey in uncovering the
          country’s most remarkable dining destinations. The MICHELIN Guide
          Manila and Environs | Cebu 2026 was launched on October 30, 2025, in a
          one-day celebration held at the Marriott Grand Ballroom in Pasay City.
          The launch of the MICHELIN Guide in the Philippines marks more than
          just a recognition of culinary excellence, it serves as a global
          platform for showcasing Filipino talent and the Philippines’ rising
          prominence on the world’s culinary stage.
        </p>
        <p>
          Brothers Andre and Edouard Michelin founded their tire company with a
          vision to advance the French automobile industry. To encourage travel
          and in turn, tire sales, they created a guide offering motorists
          useful information such as maps, petrol stations, and lodging or
          dining options. Over time, their restaurant listings grew traction and
          became a standout, leading the brothers to hire a team of mystery
          diners to visit and review restaurants anonymously. This marked the
          beginning of the prestigious MICHELIN Guide as we know it today.
        </p>
        <div className="flex flex-col gap-1">
          <div className="flex flex-col md:flex-row gap-4">
            <Image
              alt="Michelin Guide Ceremony Manila and Environs | Cebu 2026"
              className="object-cover"
              height={700}
              src={`${strapiAssetBaseUrl}/3_e0b22ba4ab.jpg`}
              width={1000}
            />
          </div>
          <caption>
            International Director of The MICHELIN Guide, Gwendal Poullennec.
          </caption>
          <caption>Photo credit: MICHELIN Guide.</caption>
        </div>
        <p>
          The MICHELIN Guide Ceremony was a momentous occasion recognizing the
          remarkable talents of Filipino chefs and restaurateurs. For the first
          time ever, restaurants in the Philippines were awarded their
          long-awaited MICHELIN Stars with <strong>one restaurant</strong>{' '}
          earning the prestigious{' '}
          <strong>Two MICHELIN Stars, eight restaurants</strong> receiving{' '}
          <strong>One MICHELIN Star, 25 Bib Gourmand</strong> distinctions
          (awarded to restaurants offering high quality, good value cooking, at
          a reasonable price), and <strong>74 MICHELIN Selected</strong>{' '}
          restaurants recognized for their outstanding culinary standards.
        </p>
        <p className="italic">
          See the{' '}
          <Link href="https://guide.michelin.com/ph/en/article/michelin-guide-ceremony/full-list-michelin-guide-ph-2026">
            full list
          </Link>{' '}
          of awarded and recognized restaurants in the MICHELIN Guide Manila and
          Environs | Cebu 2026
        </p>
        <div className="flex flex-col gap-1">
          <div className="flex flex-col md:flex-row gap-4">
            <Image
              alt="Michelin Guide Ceremony Manila and Environs | Cebu 2026"
              className="object-cover"
              height={700}
              src={`${strapiAssetBaseUrl}/4_800ee74094.jpg`}
              width={1000}
            />
          </div>
          <caption>
            2026 MICHELIN Star Awardees. Photo credit: MICHELIN Guide.
          </caption>
        </div>
        <p>
          <strong>
            Sub-Zero and Wolf proudly joined as the exclusive Event Partner
          </strong>{' '}
          of the MICHELIN Guide Ceremony Manila and Environs | Cebu 2026 in the
          Kitchen Appliances Category, reinforcing its shared commitment to
          culinary excellence and innovation. This partnership is built on
          shared passion for craftsmanship, performance, and the pursuit of
          perfection in the culinary arts. It represents a meeting of two worlds
          driven by the same philosophy: that excellence is achieved through
          dedication, innovation, and a deep respect for the ingredients and
          tools that bring each creation to life. Through this collaboration,
          Sub-Zero and Wolf stand alongside the MICHELIN Guide in honoring the
          mastery and passion that define the culinary journey.
        </p>
        <div className="flex flex-col gap-1">
          <div className="flex flex-col md:flex-row gap-4">
            <Image
              alt="Michelin Guide Ceremony Manila and Environs | Cebu 2026"
              className="object-cover"
              height={700}
              src={`${strapiAssetBaseUrl}/5_8aa8dd7b5f.jpg`}
              width={900}
            />
          </div>
          <caption>
            [L-R] Jean-Francois Darre, Focus Global Inc.Co-Founder and Executive
            Vice President, Loli Sy, International Director of The MICHELIN
            Guide, Gwendal Poullennec, Focus Global Inc. Chief Financial
            Officer, Angela Sy
          </caption>
        </div>
        <p>
          Sub-Zero and Wolf took part in the MICHELIN Guide Ceremony through an
          immersive photo experience featuring the{' '}
          <strong> Sub-Zero PRO Refrigerator</strong> and the{' '}
          <strong>Wolf Dual Fuel Range</strong>, both icons of precision,
          performance, and design. Guests were invited to take photos
          highlighting the Sub-Zero and Wolf as the statement pieces of luxury
          kitchens. It was a tribute to the true stars of the kitchen: the chefs
          from MICHELIN Guide recognized restaurants and the exceptional tools
          that help bring their culinary visions to life. The evening was a
          celebration of camaraderie, honoring the culinary arts and the
          boundless talent of Filipino Chefs.
        </p>
      </div>
      <div>
        <div className="flex flex-col gap-1">
          <Carousel
            imageURLs={carouselImages}
            aspectRatio="[9/16]"
            name="Exclusive Showroom"
            swipper="size-full md:size-1/2"
          />
          <caption>
            Photos from the Sub-Zero and Wolf booth at the MICHELIN Guide
            Ceremony.
          </caption>
          <caption>
            Photo credits: Focus Global Inc., Sub-Zero and Wolf.
          </caption>
        </div>
        <p>
          Together, Sub-Zero and Wolf, alongside the MICHELIN Guide, aim to
          inspire culinary excellence, elevate the art of cooking and empower
          both professional chefs and passionate home cooks to pursue perfection
          in every creation. The{' '}
          <strong>MICHELIN Guide Manila and Environs | Cebu 2026</strong> is
          just the beginning of Sub-Zero and Wolf’s mark among the stars.
        </p>

        <div className="flex flex-col gap-1">
          <div className="flex flex-col md:flex-row gap-4">
            <Image
              alt="Michelin Guide Ceremony Manila and Environs | Cebu 2026"
              className="object-cover"
              height={600}
              src={`${strapiAssetBaseUrl}/6_573584c533.jpg`}
              width={900}
            />
          </div>
          <caption>
            Sub-Zero and Wolf participated as an Event Partner of the MICHELIN
            Guide Manila and Environs | Cebu 2026.
          </caption>
          <caption>Photo credit: MICHELIN Guide.</caption>
        </div>
        <p>
          To know more about Sub-Zero and Wolf products, you may check their
          Instagram page,{' '}
          <Link href="https://www.instagram.com/subzerowolfph/">
            @subzerowolfph
          </Link>
          . Focus Global, Inc. is the exclusive distributor of Sub-Zero and Wolf
          here in the Philippines.
        </p>
      </div>
    </article>
  );
}

export function Index(props: IndexProps) {
  const {
    featuredFriendsOfTheKitchenPageDetails,
    featuredNewsAndEvents,
    newsAndEventsArticle
  } = props;

  const router = useRouter();

  if (router.isFallback) {
    return <LoadingIcon />;
  }

  const breadcrumbs = {
    route: newsAndEventsArticle.attributes.slug,
    tabLabel: newsAndEventsArticle.attributes.news_title
  };

  const newsAndEvents = {
    route: pageRoute.NEWS_AND_EVENTS,
    tabLabel: 'NEWS AND EVENTS'
  };

  return (
    <>
      <TabTitle page={MetaPages.NEWS_AND_EVENTS} />
      <ContainerWithSidebar
        featuredContents={
          <FeaturesSidebar
            friendsOfTheKitchenPageDetails={
              featuredFriendsOfTheKitchenPageDetails
            }
            newsAndEventsContent={featuredNewsAndEvents}
          />
        }
      >
        <Breadcrumbs
          color="text-breadcrumbGray"
          subTab={breadcrumbs}
          tab={newsAndEvents}
        />
        <Container>
          <div className="mt-0 sm:mt-10" />
          <ArticleContent />
          <div className="flex justify-end ml-auto mt-10">
            <ButtonLink
              buttonTitle="BACK"
              ctaLink={pageRoute.NEWS_AND_EVENTS}
            />
          </div>
          <MarginSpacing margin="mb-20" />
        </Container>
      </ContainerWithSidebar>
      <MarginSpacing margin="mb-5" />
      <SocialMediaShareSection />
    </>
  );
}

export async function getStaticProps() {
  const featuredFriendsOfTheKitchenPageDetails =
    await getFeaturedFriendsOfTheKitchenPageDetails();
  const featuredNewsAndEvents = await getFeaturedNewsAndEvents();
  const newsAndEventsArticle = {
    attributes: {
      slug: '103025',
      news_title: 'Celebrating the Stars of the Kitchen'
    }
  } as NewsAndEventsModel;

  return {
    props: {
      featuredFriendsOfTheKitchenPageDetails,
      featuredNewsAndEvents,
      newsAndEventsArticle
    },
    revalidate: 300
  };
}

export default Index;
